import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import {
  Package, Plus, LogOut, Truck, MapPin, User, Phone,
  Clock, CheckCircle, Box, FileText, QrCode, X, Eye, Lock, KeyRound, XCircle,
  PackagePlus, Trash2, ArrowRightLeft
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  RECEIVED: { icon: Box, color: "bg-slate-500", label: "Received" },
  SHIPPED: { icon: Package, color: "bg-blue-500", label: "Shipped" },
  IN_TRANSIT: { icon: Truck, color: "bg-amber-500", label: "In Transit" },
  OUT_FOR_DELIVERY: { icon: MapPin, color: "bg-purple-500", label: "Out for Delivery" },
  DELIVERED: { icon: CheckCircle, color: "bg-amber-500", label: "Delivered" },
};

const REQUEST_STATUS_CONFIG = {
  PENDING: { color: "bg-amber-500", label: "Pending" },
  APPROVED: { color: "bg-amber-500", label: "Approved" },
  REJECTED: { color: "bg-red-500", label: "Rejected" },
};

const CustomerDashboard = () => {
  const [shipments, setShipments] = useState([]);
  const [requests, setRequests] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedTracking, setSelectedTracking] = useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [myPackages, setMyPackages] = useState([]);
  const [addPackageOpen, setAddPackageOpen] = useState(false);
  const navigate = useNavigate();

  const token = localStorage.getItem("token");
  const userName = localStorage.getItem("userName");

  const axiosAuth = axios.create({
    headers: { Authorization: `Bearer ${token}` },
  });

  useEffect(() => {
    if (!token || localStorage.getItem("userType") !== "customer") {
      navigate("/customer");
      return;
    }
    fetchData();
  }, [token, navigate]);

  const fetchData = async () => {
    try {
      const [shipmentsRes, requestsRes, profileRes, packagesRes] = await Promise.all([
        axiosAuth.get(`${API}/customer/shipments`),
        axiosAuth.get(`${API}/customer/requests`),
        axiosAuth.get(`${API}/customer/profile`),
        axiosAuth.get(`${API}/packages/customer`)
      ]);
      setShipments(shipmentsRes.data);
      setRequests(requestsRes.data);
      setProfile(profileRes.data);
      setMyPackages(packagesRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.clear();
        navigate("/customer");
      }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/customer");
  };

  const openQrDialog = (trackingNumber) => {
    setSelectedTracking(trackingNumber);
    setQrDialogOpen(true);
  };

  const viewInvoice = async (shipmentId) => {
    setInvoiceLoading(true);
    try {
      const res = await axiosAuth.get(`${API}/invoices/customer/shipment/${shipmentId}`);
      setSelectedInvoice(res.data);
      setInvoiceDialogOpen(true);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.info("No invoice available for this shipment yet");
      } else {
        toast.error("Failed to load invoice");
      }
    } finally {
      setInvoiceLoading(false);
    }
  };

  const getStatusConfig = (status) => STATUS_CONFIG[status] || STATUS_CONFIG.RECEIVED;

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm("Remove this package?")) return;
    try {
      await axiosAuth.delete(`${API}/packages/customer/${packageId}`);
      setMyPackages(myPackages.filter((p) => p.id !== packageId));
      toast.success("Package removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove package");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500 dark:text-slate-400 font-mono text-sm uppercase tracking-widest">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo-v2.png" alt="SHIPP SMART" className="h-20 w-auto" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-slate-400 text-sm" data-testid="customer-name">Welcome, {userName}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
              data-testid="customer-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: "Total Shipments", value: shipments.length, color: "bg-slate-900" },
            { label: "In Transit", value: shipments.filter(s => s.status === "IN_TRANSIT").length, color: "bg-amber-500" },
            { label: "Delivered", value: shipments.filter(s => s.status === "DELIVERED").length, color: "bg-amber-500" },
            { label: "Pending Requests", value: requests.filter(r => r.request_status === "PENDING").length, color: "bg-blue-500" },
          ].map((stat) => (
            <motion.div key={stat.label} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-6">
                  <div className={`w-3 h-3 ${stat.color} mb-4`} />
                  <p className="text-slate-500 dark:text-slate-400 text-xs uppercase tracking-widest mb-1">{stat.label}</p>
                  <p className="font-chivo font-black text-3xl text-slate-900 dark:text-white">{stat.value}</p>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Tabs */}
        <Tabs defaultValue="shipments" className="space-y-6">
          <div className="flex justify-between items-center">
            <TabsList className="bg-slate-200 dark:bg-slate-800">
              <TabsTrigger value="shipments" data-testid="shipments-tab">My Shipments</TabsTrigger>
              <TabsTrigger value="packages" data-testid="my-packages-tab">
                My Packages {myPackages.filter(p => p.status === "PENDING").length > 0 && (
                  <Badge className="ml-2 bg-amber-500 text-white text-[10px]">
                    {myPackages.filter(p => p.status === "PENDING").length}
                  </Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="requests" data-testid="requests-tab">My Requests</TabsTrigger>
              <TabsTrigger value="profile" data-testid="profile-tab">Profile</TabsTrigger>
            </TabsList>
            <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest text-xs"
                  data-testid="create-request-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  New Request
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-chivo font-bold text-xl uppercase tracking-tight">Request Shipment</DialogTitle>
                </DialogHeader>
                <CreateRequestForm
                  profile={profile}
                  onSuccess={() => {
                    setCreateDialogOpen(false);
                    fetchData();
                  }}
                  axiosAuth={axiosAuth}
                />
              </DialogContent>
            </Dialog>
          </div>

          {/* Shipments Tab */}
          <TabsContent value="shipments">
            {shipments.length === 0 ? (
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" strokeWidth={1} />
                  <h3 className="font-chivo font-bold text-lg text-slate-900 dark:text-white mb-2">No Shipments Yet</h3>
                  <p className="text-slate-500 dark:text-slate-400">Your shipments will appear here once assigned</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="customer-shipments-list">
                {shipments.map((shipment, index) => {
                  const statusConfig = getStatusConfig(shipment.status);
                  const StatusIcon = statusConfig.icon;
                  return (
                    <motion.div
                      key={shipment.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800 hover:border-slate-300 dark:hover:border-slate-600 transition-colors">
                        <CardContent className="p-6">
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex items-start gap-4">
                              <div className={`w-12 h-12 ${statusConfig.color}/10 flex items-center justify-center shrink-0`}>
                                <StatusIcon className={`w-6 h-6 ${statusConfig.color.replace('bg-', 'text-')}`} strokeWidth={1.5} />
                              </div>
                              <div>
                                <div className="flex items-center gap-3 mb-2">
                                  <span className="font-mono font-bold text-slate-900 dark:text-white">{shipment.tracking_number}</span>
                                  <Badge className={`${statusConfig.color} text-white text-[10px] uppercase tracking-widest`}>
                                    {statusConfig.label}
                                  </Badge>
                                </div>
                                <p className="text-slate-600 dark:text-slate-300 text-sm">{shipment.origin} → {shipment.destination}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => viewInvoice(shipment.id)}
                                disabled={invoiceLoading}
                                data-testid={`invoice-btn-${shipment.id}`}
                              >
                                <FileText className="w-4 h-4 mr-2" />
                                Invoice
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openQrDialog(shipment.tracking_number)}
                                data-testid={`qr-btn-${shipment.tracking_number}`}
                              >
                                <QrCode className="w-4 h-4 mr-2" />
                                QR Code
                              </Button>
                              <Link to={`/track/${shipment.tracking_number}`}>
                                <Button variant="outline" size="sm">
                                  <Eye className="w-4 h-4 mr-2" />
                                  Track
                                </Button>
                              </Link>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Packages Tab */}
          <TabsContent value="packages">
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-slate-500 dark:text-slate-400 text-sm">
                  {myPackages.length} package{myPackages.length !== 1 ? "s" : ""} registered
                </p>
                <Button
                  onClick={() => setAddPackageOpen(true)}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest text-xs"
                  data-testid="add-package-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Package
                </Button>
              </div>

              {myPackages.length === 0 ? (
                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <CardContent className="p-12 text-center">
                    <Package className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" strokeWidth={1} />
                    <h3 className="font-chivo font-bold text-lg text-slate-900 dark:text-white mb-2">No Packages Registered</h3>
                    <p className="text-slate-500 dark:text-slate-400 mb-4 text-sm">Pre-register your incoming packages so we can notify you when they arrive at our warehouse.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3" data-testid="my-packages-list">
                  {myPackages.map((pkg) => (
                    <Card key={pkg.id} className={`border-slate-200 dark:border-slate-700 dark:bg-slate-800 transition-colors ${pkg.status === "MATCHED" ? "border-l-4 border-l-green-500" : ""}`}>
                      <CardContent className="p-5">
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex items-start gap-3 min-w-0">
                            <Package className={`w-5 h-5 shrink-0 mt-0.5 ${pkg.status === "MATCHED" ? "text-green-500" : "text-slate-400 dark:text-slate-500"}`} />
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono font-bold text-slate-900 dark:text-white text-sm">{pkg.tracking_number}</span>
                                <Badge className={`${pkg.status === "MATCHED" ? "bg-green-500" : "bg-amber-500"} text-white text-[10px] uppercase tracking-widest`}>
                                  {pkg.status === "MATCHED" ? "Matched" : "Waiting"}
                                </Badge>
                              </div>
                              <p className="text-slate-700 dark:text-slate-200 text-sm font-medium">{pkg.name}</p>
                              {pkg.description && <p className="text-slate-500 dark:text-slate-400 text-xs">{pkg.description}</p>}
                              {pkg.status === "MATCHED" && (
                                <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
                                  <ArrowRightLeft className="w-3 h-3" />
                                  Package received — shipment created!
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="shrink-0">
                            {pkg.status === "PENDING" && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePackage(pkg.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                data-testid={`delete-pkg-${pkg.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Requests Tab */}
          <TabsContent value="requests">
            {requests.length === 0 ? (
              <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                <CardContent className="p-12 text-center">
                  <FileText className="w-16 h-16 text-slate-300 dark:text-slate-600 mx-auto mb-4" strokeWidth={1} />
                  <h3 className="font-chivo font-bold text-lg text-slate-900 dark:text-white mb-2">No Requests Yet</h3>
                  <p className="text-slate-500 dark:text-slate-400 mb-4">Create a shipment request to get started</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="customer-requests-list">
                {requests.map((request, index) => {
                  const statusConfig = REQUEST_STATUS_CONFIG[request.request_status];
                  return (
                    <motion.div
                      key={request.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                        <CardContent className="p-6">
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div>
                              <div className="flex items-center gap-3 mb-2">
                                <Badge className={`${statusConfig.color} text-white text-[10px] uppercase tracking-widest`}>
                                  {statusConfig.label}
                                </Badge>
                                {request.estimated_price && (
                                  <span className="text-slate-500 dark:text-slate-400 text-sm">Est. ${request.estimated_price}</span>
                                )}
                              </div>
                              <p className="text-slate-900 dark:text-white font-medium">{request.origin} → {request.destination}</p>
                              <p className="text-slate-500 dark:text-slate-400 text-sm">To: {request.receiver.name}, {request.receiver.city}</p>
                              {request.admin_notes && (
                                <p className="text-red-500 text-sm mt-2">Note: {request.admin_notes}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <p className="text-slate-400 dark:text-slate-500 text-xs font-mono">
                                {new Date(request.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            {profile && (
              <div className="space-y-6">
                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="font-chivo uppercase tracking-tight dark:text-white">My Profile</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Name</Label>
                        <p className="font-medium text-slate-900 dark:text-white" data-testid="profile-name">{profile.name}</p>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Email</Label>
                        <p className="font-medium text-slate-900 dark:text-white" data-testid="profile-email">{profile.email}</p>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Phone</Label>
                        <p className="font-medium text-slate-900 dark:text-white">{profile.phone}</p>
                      </div>
                      <div>
                        <Label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">City</Label>
                        <p className="font-medium text-slate-900 dark:text-white">{profile.city || "Not set"}</p>
                      </div>
                      <div className="md:col-span-2">
                        <Label className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400">Address</Label>
                        <p className="font-medium text-slate-900 dark:text-white">{profile.address || "Not set"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Change Password Card */}
                <Card className="border-slate-200 dark:border-slate-700 dark:bg-slate-800">
                  <CardHeader>
                    <CardTitle className="font-chivo uppercase tracking-tight dark:text-white flex items-center gap-2">
                      <KeyRound className="w-5 h-5 text-amber-500" />
                      Security
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">Password</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">Change your account password</p>
                      </div>
                      <Button
                        onClick={() => setChangePasswordOpen(true)}
                        variant="outline"
                        className="border-amber-500 text-amber-600 hover:bg-amber-50"
                        data-testid="change-password-btn"
                      >
                        <Lock className="w-4 h-4 mr-2" />
                        Change Password
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* QR Code Dialog */}
        <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-chivo uppercase tracking-tight text-center">Tracking QR Code</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col items-center p-6">
              {selectedTracking && (
                <>
                  <div className="bg-white p-4 rounded-lg shadow-inner mb-4">
                    <QRCodeSVG
                      value={`${window.location.origin}/track/${selectedTracking}`}
                      size={200}
                      level="H"
                      includeMargin
                    />
                  </div>
                  <p className="font-mono text-lg font-bold text-slate-900 dark:text-white" data-testid="qr-tracking-number">{selectedTracking}</p>
                  <p className="text-slate-500 dark:text-slate-400 text-sm mt-2">Scan to track this shipment</p>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>

        {/* Change Password Dialog */}
        <Dialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-chivo uppercase tracking-tight">Change Password</DialogTitle>
            </DialogHeader>
            <ChangePasswordForm 
              axiosAuth={axiosAuth}
              onSuccess={() => {
                setChangePasswordOpen(false);
                toast.success("Password changed successfully!");
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Invoice View Dialog */}
        <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
            {selectedInvoice && (
              <InvoiceViewCustomer
                invoice={selectedInvoice}
                onClose={() => {
                  setInvoiceDialogOpen(false);
                  setSelectedInvoice(null);
                }}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* Add Package Dialog */}
        <Dialog open={addPackageOpen} onOpenChange={setAddPackageOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-chivo font-bold uppercase tracking-tight">
                Pre-register Package
              </DialogTitle>
            </DialogHeader>
            <AddPackageForm
              axiosAuth={axiosAuth}
              onSuccess={() => {
                setAddPackageOpen(false);
                fetchData();
              }}
            />
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

// Create Request Form
const CreateRequestForm = ({ profile, onSuccess, axiosAuth }) => {
  const [formData, setFormData] = useState({
    sender: {
      name: profile?.name || "",
      address: profile?.address || "",
      city: profile?.city || "",
      phone: profile?.phone || ""
    },
    receiver: { name: "", address: "", city: "", phone: "" },
    origin: profile?.city || "",
    destination: "",
    weight: "",
    volume: "",
    description: ""
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (section, field, value) => {
    if (section) {
      setFormData(prev => ({
        ...prev,
        [section]: { ...prev[section], [field]: value }
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...formData,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        volume: formData.volume ? parseFloat(formData.volume) : null
      };
      await axiosAuth.post(`${API}/customer/requests`, payload);
      toast.success("Request submitted successfully!");
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Sender */}
      <div>
        <h4 className="font-chivo font-bold text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
          <User className="w-4 h-4" /> Sender (You)
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Name</Label>
            <Input value={formData.sender.name} onChange={(e) => handleChange("sender", "name", e.target.value)} required data-testid="req-sender-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Phone</Label>
            <Input value={formData.sender.phone} onChange={(e) => handleChange("sender", "phone", e.target.value)} required data-testid="req-sender-phone" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">Address</Label>
            <Input value={formData.sender.address} onChange={(e) => handleChange("sender", "address", e.target.value)} required data-testid="req-sender-address" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">City</Label>
            <Input value={formData.sender.city} onChange={(e) => handleChange("sender", "city", e.target.value)} required data-testid="req-sender-city" />
          </div>
        </div>
      </div>

      {/* Receiver */}
      <div>
        <h4 className="font-chivo font-bold text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
          <MapPin className="w-4 h-4" /> Receiver
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Name</Label>
            <Input value={formData.receiver.name} onChange={(e) => handleChange("receiver", "name", e.target.value)} required data-testid="req-receiver-name" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Phone</Label>
            <Input value={formData.receiver.phone} onChange={(e) => handleChange("receiver", "phone", e.target.value)} required data-testid="req-receiver-phone" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">Address</Label>
            <Input value={formData.receiver.address} onChange={(e) => handleChange("receiver", "address", e.target.value)} required data-testid="req-receiver-address" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">City</Label>
            <Input value={formData.receiver.city} onChange={(e) => handleChange("receiver", "city", e.target.value)} required data-testid="req-receiver-city" />
          </div>
        </div>
      </div>

      {/* Shipment Details */}
      <div>
        <h4 className="font-chivo font-bold text-xs uppercase tracking-widest text-slate-500 mb-4 flex items-center gap-2">
          <Package className="w-4 h-4" /> Details
        </h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs uppercase tracking-widest">Origin City</Label>
            <Input value={formData.origin} onChange={(e) => handleChange(null, "origin", e.target.value)} required data-testid="req-origin" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Destination City</Label>
            <Input value={formData.destination} onChange={(e) => handleChange(null, "destination", e.target.value)} required data-testid="req-destination" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Weight (lbs)</Label>
            <Input type="number" step="0.1" value={formData.weight} onChange={(e) => handleChange(null, "weight", e.target.value)} data-testid="req-weight" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest">Volume (ft³)</Label>
            <Input type="number" step="0.01" value={formData.volume} onChange={(e) => handleChange(null, "volume", e.target.value)} data-testid="req-volume" />
          </div>
          <div className="col-span-2">
            <Label className="text-xs uppercase tracking-widest">Description</Label>
            <Textarea value={formData.description} onChange={(e) => handleChange(null, "description", e.target.value)} rows={2} data-testid="req-description" />
          </div>
        </div>
      </div>

      <Button type="submit" disabled={loading} className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest" data-testid="submit-request-btn">
        {loading ? "Submitting..." : "Submit Request"}
      </Button>
    </form>
  );
};

// Change Password Form Component
const ChangePasswordForm = ({ axiosAuth, onSuccess }) => {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      await axiosAuth.put(`${API}/customer/change-password`, {
        current_password: currentPassword,
        new_password: newPassword
      });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest">Current Password</Label>
        <Input
          type="password"
          value={currentPassword}
          onChange={(e) => setCurrentPassword(e.target.value)}
          placeholder="Enter current password"
          required
          data-testid="current-password-input"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest">New Password</Label>
        <Input
          type="password"
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          placeholder="Enter new password (min 6 characters)"
          required
          data-testid="new-password-input"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs uppercase tracking-widest">Confirm New Password</Label>
        <Input
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          placeholder="Confirm new password"
          required
          data-testid="confirm-password-input"
        />
      </div>
      <Button
        type="submit"
        disabled={loading}
        className="w-full h-10 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest"
        data-testid="submit-change-password-btn"
      >
        {loading ? "Changing..." : "Change Password"}
      </Button>
    </form>
  );
};

// Add Package Form Component
const AddPackageForm = ({ axiosAuth, onSuccess }) => {
  const [packages, setPackages] = useState([{ name: "", description: "", tracking_number: "" }]);
  const [loading, setLoading] = useState(false);

  const addRow = () => {
    setPackages([...packages, { name: "", description: "", tracking_number: "" }]);
  };

  const removeRow = (index) => {
    if (packages.length <= 1) return;
    setPackages(packages.filter((_, i) => i !== index));
  };

  const updateRow = (index, field, value) => {
    setPackages(packages.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valid = packages.filter((p) => p.name.trim() && p.tracking_number.trim());
    if (valid.length === 0) {
      toast.error("Please fill in at least one package");
      return;
    }
    setLoading(true);
    try {
      if (valid.length === 1) {
        await axiosAuth.post(`${API}/packages/customer`, valid[0]);
      } else {
        await axiosAuth.post(`${API}/packages/customer/bulk`, valid);
      }
      toast.success(`${valid.length} package${valid.length > 1 ? "s" : ""} registered!`);
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add package(s)");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <p className="text-slate-500 dark:text-slate-400 text-sm">
        Enter the tracking number(s) from your carrier. We'll automatically notify you when the package arrives at our warehouse.
      </p>
      {packages.map((pkg, index) => (
        <div key={index} className="p-3 bg-slate-50 dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-slate-400 dark:text-slate-500">Package #{index + 1}</span>
            {packages.length > 1 && (
              <Button type="button" variant="ghost" size="sm" onClick={() => removeRow(index)} className="text-red-400 hover:text-red-600 h-6 w-6 p-0">
                <X className="w-3 h-3" />
              </Button>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">Name *</Label>
              <Input
                value={pkg.name}
                onChange={(e) => updateRow(index, "name", e.target.value)}
                placeholder="e.g. Laptop from Amazon"
                required
                data-testid={`pkg-name-${index}`}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs uppercase tracking-widest">Tracking # *</Label>
              <Input
                value={pkg.tracking_number}
                onChange={(e) => updateRow(index, "tracking_number", e.target.value)}
                placeholder="e.g. 1Z999AA10123456784"
                required
                data-testid={`pkg-tracking-${index}`}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-widest">Description (optional)</Label>
            <Input
              value={pkg.description}
              onChange={(e) => updateRow(index, "description", e.target.value)}
              placeholder="Brief description"
              data-testid={`pkg-desc-${index}`}
            />
          </div>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={addRow}
        className="w-full border-dashed text-slate-500 dark:text-slate-400"
        data-testid="add-another-pkg-btn"
      >
        <Plus className="w-4 h-4 mr-2" />
        Add Another Package
      </Button>
      <Button
        type="submit"
        disabled={loading}
        className="w-full h-12 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold uppercase tracking-widest"
        data-testid="submit-packages-btn"
      >
        {loading ? "Registering..." : `Register ${packages.length} Package${packages.length > 1 ? "s" : ""}`}
      </Button>
    </form>
  );
};

// Invoice View Component for Customer
const InvoiceViewCustomer = ({ invoice, onClose }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handlePrint = () => {
    window.print();
  };

  if (!invoice) return null;

  const isPaid = invoice.payment_status === "PAID";

  return (
    <div className="bg-white dark:bg-slate-800" data-testid="customer-invoice-view">
      {/* Invoice Header */}
      <div className="bg-slate-900 text-white p-6 rounded-t-lg print:rounded-none">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-chivo font-bold text-amber-500">SHIPP SMART</h1>
            <p className="text-slate-400 text-sm mt-1">Shipping & Logistics</p>
          </div>
          <div className="text-right">
            <p className="text-amber-500 font-mono font-bold text-lg">{invoice.invoice_number}</p>
            <p className="text-slate-400 text-sm">{formatDate(invoice.date)}</p>
          </div>
        </div>
      </div>

      {/* Payment Status Banner */}
      <div className={`p-4 flex items-center gap-3 ${isPaid ? 'bg-green-50 dark:bg-green-900/20 border-b border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800'}`}>
        {isPaid ? (
          <CheckCircle className="w-6 h-6 text-green-500" />
        ) : (
          <XCircle className="w-6 h-6 text-red-500" />
        )}
        <div>
          <span className={`inline-block px-3 py-1 rounded text-sm font-bold text-white ${isPaid ? 'bg-green-500' : 'bg-red-500'}`}>
            {isPaid ? 'PAID' : 'UNPAID'}
          </span>
          {isPaid && invoice.payment_date && (
            <p className="text-green-600 dark:text-green-400 text-xs mt-1">Paid on {formatDate(invoice.payment_date)}</p>
          )}
        </div>
      </div>

      {/* Bill To */}
      <div className="p-6 border-b border-slate-200 dark:border-slate-700">
        <p className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">Bill To</p>
        <p className="font-bold text-slate-900 dark:text-white text-lg">{invoice.customer_name}</p>
      </div>

      {/* Items Table */}
      <div className="p-6">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-slate-200 dark:border-slate-600">
              <th className="text-left text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 pb-3">Description</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 pb-3">Weight</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 pb-3">Volume</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 pb-3">Price</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 pb-3">Fixed Fee</th>
              <th className="text-right text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 pb-3">Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items.map((item, index) => (
              <tr key={index} className="border-b border-slate-100 dark:border-slate-700">
                <td className="py-4 text-slate-900 dark:text-white">{item.description}</td>
                <td className="py-4 text-right text-slate-600 dark:text-slate-300 font-mono text-sm">
                  {item.weight ? `${item.weight} lbs` : '-'}
                </td>
                <td className="py-4 text-right text-slate-600 dark:text-slate-300 font-mono text-sm">
                  {item.volume ? `${item.volume} ft³` : '-'}
                </td>
                <td className="py-4 text-right text-slate-600 dark:text-slate-300 font-mono text-sm">
                  ${(item.price || 0).toFixed(2)}
                </td>
                <td className="py-4 text-right text-slate-600 dark:text-slate-300 font-mono text-sm">
                  ${(item.fixed_fee || 0).toFixed(2)}
                </td>
                <td className="py-4 text-right font-mono font-bold text-slate-900 dark:text-white">
                  ${(item.total || 0).toFixed(2)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Totals */}
      <div className="bg-slate-50 dark:bg-slate-900/50 p-6 print:rounded-none">
        <div className="max-w-xs ml-auto space-y-2">
          <div className="flex justify-between text-slate-600 dark:text-slate-300">
            <span>Subtotal:</span>
            <span className="font-mono">${(invoice.subtotal || 0).toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-slate-600 dark:text-slate-300">
            <span>Shipping Fee:</span>
            <span className="font-mono">${(invoice.shipping_fee || 0).toFixed(2)}</span>
          </div>
          {invoice.discount > 0 && (
            <div className="flex justify-between text-green-600 dark:text-green-400">
              <span>Discount:</span>
              <span className="font-mono">-${(invoice.discount || 0).toFixed(2)}</span>
            </div>
          )}
          <div className="flex justify-between text-xl font-bold text-slate-900 dark:text-white border-t border-slate-300 dark:border-slate-600 pt-3 mt-3">
            <span>Grand Total:</span>
            <span className="font-mono text-amber-600">${(invoice.grand_total || 0).toFixed(2)}</span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {invoice.notes && (
        <div className="p-6 border-t border-slate-200 dark:border-slate-700">
          <p className="text-xs uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">Notes</p>
          <p className="text-slate-600 dark:text-slate-300">{invoice.notes}</p>
        </div>
      )}

      {/* Actions (hidden on print) */}
      <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex gap-3 print:hidden">
        <Button
          onClick={handlePrint}
          className="flex-1 bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
        >
          <FileText className="w-4 h-4 mr-2" />
          Print / Download
        </Button>
        {onClose && (
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        )}
      </div>
    </div>
  );
};

export default CustomerDashboard;
